/**
 * Dashboard Tabs Settings Tab Component (CR-4441)
 * Allows admins to configure which dashboard tabs are visible for customers vs suppliers.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  LayoutDashboard, BarChart3, PieChart, MapPin, DollarSign,
  Leaf, ShieldCheck, CalendarClock, CalendarCheck, Truck, Star,
  Building2, type LucideIcon,
} from 'lucide-react';
import { DASHBOARD_TAB_REGISTRY } from '../../dashboard/types';
import type { DashboardTabsVisibility } from './types';

const TAB_ICONS: Record<string, LucideIcon> = {
  'default': LayoutDashboard, 'group-kpi': BarChart3, 'group-detail-kpi': PieChart,
  'site-kpi': MapPin, 'cost': DollarSign, 'emissions': Leaf, 'compliance': ShieldCheck,
  'future-forecast': CalendarClock, 'date-adherence': CalendarCheck, 'expediting': Truck,
  'performance-ratings': Star,
};

interface DashboardTabsTabProps {
  dashboardTabs: DashboardTabsVisibility;
  supplierDashboardTabs: DashboardTabsVisibility;
  saving: boolean;
  onDashboardTabsChange: (tabs: DashboardTabsVisibility) => void;
  onSupplierDashboardTabsChange: (tabs: DashboardTabsVisibility) => void;
  onSave: () => void;
}

export function DashboardTabsTab({
  dashboardTabs,
  supplierDashboardTabs,
  saving,
  onDashboardTabsChange,
  onSupplierDashboardTabsChange,
  onSave,
}: DashboardTabsTabProps) {
  const NEW_TABS_DEFAULT_OFF = new Set(['scheduled', 'spot-orders']);
  const customerEnabled = DASHBOARD_TAB_REGISTRY.filter(tab => dashboardTabs[tab.id] ?? !NEW_TABS_DEFAULT_OFF.has(tab.id)).length;
  const supplierEnabled = DASHBOARD_TAB_REGISTRY.filter(tab => supplierDashboardTabs[tab.id] ?? false).length;
  const totalCount = DASHBOARD_TAB_REGISTRY.length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5" />
            Dashboard Tab Visibility
          </CardTitle>
          <CardDescription>
            Control which dashboard tabs are visible for customer and supplier users independently.
          </CardDescription>
          <div className="flex items-center gap-4 pt-2 text-sm text-muted-foreground">
            <span><Building2 className="h-4 w-4 inline mr-1" />Customer: {customerEnabled}/{totalCount}</span>
            <span><Truck className="h-4 w-4 inline mr-1" />Supplier: {supplierEnabled}/{totalCount}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Header row */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg font-medium text-sm">
            <span className="flex-1">Tab</span>
            <div className="flex gap-8">
              <span className="w-20 text-center flex items-center gap-1"><Building2 className="h-4 w-4" />Customer</span>
              <span className="w-20 text-center flex items-center gap-1"><Truck className="h-4 w-4" />Supplier</span>
            </div>
          </div>

          {DASHBOARD_TAB_REGISTRY.map((tab) => {
            const Icon = TAB_ICONS[tab.id] || LayoutDashboard;
            return (
              <div key={tab.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-3 flex-1">
                  <Icon className="h-5 w-5 text-primary" />
                  <div>
                    <Label className="text-base font-medium">{tab.label}</Label>
                    <p className="text-sm text-muted-foreground">{tab.description}</p>
                  </div>
                </div>
                <div className="flex gap-8">
                  <div className="w-20 flex justify-center">
                    <Switch
                      checked={dashboardTabs[tab.id] ?? !NEW_TABS_DEFAULT_OFF.has(tab.id)}
                      onCheckedChange={(checked) => onDashboardTabsChange({ ...dashboardTabs, [tab.id]: checked })}
                    />
                  </div>
                  <div className="w-20 flex justify-center">
                    <Switch
                      checked={supplierDashboardTabs[tab.id] ?? false}
                      onCheckedChange={(checked) => onSupplierDashboardTabsChange({ ...supplierDashboardTabs, [tab.id]: checked })}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <Button onClick={onSave} disabled={saving} className="w-full mt-4">
            {saving ? 'Saving...' : 'Save Dashboard Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
